import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import DocumentLibrary from "@/pages/DocumentLibrary";
import Settings from "@/pages/Settings";
import Tracker from "@/pages/Tracker";
import Commitments from "@/pages/Commitments";
import Sessions from "@/pages/Sessions";
import Layout from "@/components/Layout";

// Auth Context
const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // One-shot session restore. localStorage is a browser global and
    // setIsAuthenticated/setUserEmail/setLoading are stable React setters.
    const storedEmail = localStorage.getItem('stryde_email');
    if (storedEmail) {
      setIsAuthenticated(true);
      setUserEmail(storedEmail);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = (email) => {
    localStorage.setItem('stryde_email', email);
    setIsAuthenticated(true);
    setUserEmail(email);
  };

  const handleLogout = () => {
    localStorage.removeItem('stryde_email');
    setIsAuthenticated(false);
    setUserEmail(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 spinner" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, userEmail, handleLogin, handleLogout }}>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route 
              path="/login" 
              element={
                isAuthenticated ? 
                  <Navigate to="/" replace /> : 
                  <Login onLogin={handleLogin} />
              } 
            />
            <Route 
              element={
                isAuthenticated ? 
                  <Layout userEmail={userEmail} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              }
            >
              <Route path="/" element={<Dashboard />} />
              <Route path="/tracker" element={<Tracker />} />
              <Route path="/sessions" element={<Sessions />} />
              <Route path="/commitments" element={<Commitments />} />
              <Route path="/documents" element={<DocumentLibrary />} />
              <Route path="/settings" element={<Settings />} />
              {/* Legacy paths -> redirect to tracker */}
              <Route path="/new" element={<Navigate to="/tracker" replace />} />
              <Route path="/feasibility/:id" element={<Navigate to="/tracker" replace />} />
              <Route path="/sessions/:id" element={<Navigate to="/tracker" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthContext.Provider>
  );
}

export default App;
